import {
  reduce,
  sortBy,
  values,
  keyBy,
  map,
  size,
  isEmpty,
  filter,
  includes,
  get,
} from "lodash";

import {
  getFormattedMsg,
  isContentEmpty,
} from "src/components/RichTextContent/RichTextContent.util";
import { TAG_LOAD_LIMIT, TAGS_UI_COMPONENTS } from "src/util/consts";
import type { TreeNodeData } from "src/shared/TreeViewNext";

/** Shape of a node used by TagsNavigation and TagTypeNavigation trees. */
export type TagNavNode = TreeNodeData & {
  active?: boolean | undefined;
  toggled?: boolean | undefined;
  loading?: boolean | undefined;
  disabled?: boolean | undefined;
  isTagType?: boolean | undefined;
  isTagValue?: boolean | undefined;
  isTagTypeDesc?: boolean | undefined;
  isEmptyStateMessage?: boolean | undefined;
  isLoadMore?: boolean | undefined;
  details?: unknown;
  tagValue?: Record<string, unknown> | null | undefined;
  children?: TagNavNode[] | null | undefined;
};

export const filterTagTypes = (tagTypes: unknown) =>
  filter(
    tagTypes as Record<string, unknown>[],
    (config) =>
      includes(
        (config as Record<string, unknown>)?.["visibleIn"] as unknown[],
        TAGS_UI_COMPONENTS.NAVIGATION
      ) &&
      !(config as Record<string, unknown>)?.["isReserved"] &&
      !(config as Record<string, unknown>)?.["isGroupAssociation"]
  );

export const getChildren = ({
  tagType,
  tags,
  activeTag,
  activeTagId,
  loading,
}: {
  tagType: string;
  tags: Record<
    string,
    | { tags?: unknown[] | undefined; totalCount?: number | undefined }
    | undefined
  >;
  activeTag?: { id: string | number } | null;
  activeTagId?: string | undefined;
  loading?: boolean;
}): TagNavNode[] => {
  if (!tagType) {
    return [];
  }

  const tagBucket = tags[tagType];
  if (!tagBucket) {
    if (loading) {
      return [
        {
          id: `${tagType}-loading`,
          name: "Loading...",
          active: false,
          toggled: false,
          children: null,
          loading: true,
        },
      ];
    }
    return [];
  }

  const totalTagCount = tagBucket.totalCount ?? 0;

  const children: TagNavNode[] = map(
    tagBucket.tags ?? [],
    (tag: Record<string, unknown>) => ({
      id: tag["id"] as string,
      name: tag["displayValue"] as string,
      active:
        tag["id"] === activeTag?.id ||
        (activeTagId != null && String(tag["id"]) === String(activeTagId)),
      toggled: false,
      isTagValue: true,
      tagValue: tag as Record<string, unknown>,
      children: null,
    })
  );

  if (loading) {
    return [
      ...children,
      {
        id: `${tagType}-loading`,
        name: "Loading...",
        active: false,
        toggled: false,
        children: null,
        loading: true,
      },
    ];
  }

  if (size(children) < totalTagCount) {
    const remaining = totalTagCount - size(children);

    return [
      ...children,
      {
        id: tagType,
        name: `+${remaining} tags, click to load next ${
          remaining < TAG_LOAD_LIMIT ? remaining : TAG_LOAD_LIMIT
        }`,
        active: false,
        toggled: false,
        isLoadMore: true,
        tagValue: null,
        children: null,
      },
    ];
  }

  return children;
};

export const buildTagsTree = ({
  tagTypes,
  tags,
  tree = [],
  activeTag,
  activeTagId,
  tagTypesConfig,
  tagsLoading = {},
}: {
  tagTypes: unknown;
  tags: Record<
    string,
    | { tags?: unknown[] | undefined; totalCount?: number | undefined }
    | undefined
  >;
  tree?: TagNavNode[] | undefined;
  activeTag?:
    | { id: string | number; type?: string | undefined }
    | null
    | undefined;
  activeTagId?: string | undefined;
  tagTypesConfig?: Record<string, unknown> | undefined;
  tagsLoading?: Record<string, boolean> | undefined;
}): TagNavNode[] => {
  const treeById = keyBy(tree, "id");

  const tagTypesTree = reduce(
    sortBy(tagTypes as Record<string, unknown>[], "name"),
    (prev: Record<string, TagNavNode>, tagType: Record<string, unknown>) => {
      const id = tagType["id"] as string;
      const details = tagType["details"];
      const name = tagType["name"] as string | undefined;

      const node = treeById[id];
      const { active = false, toggled = false } = node ?? {};

      const loading = !!tagsLoading[id];

      const rawChildren = getChildren({
        tagType: id,
        tags,
        activeTag: activeTag ?? null,
        activeTagId,
        loading,
      });

      const isViewingTagTypeExplore =
        !!activeTagId &&
        !isEmpty(get(tagTypesConfig, activeTagId, {})) &&
        activeTagId === id;

      // Build prefix nodes declaratively to avoid mutating the children array.
      const parsedDetails = getFormattedMsg(details);
      const prefixNodes: TagNavNode[] = [];

      if (details && !isContentEmpty(parsedDetails)) {
        prefixNodes.push({
          id: `${id}-desc`,
          details: parsedDetails,
          active: false,
          toggled: false,
          isTagTypeDesc: true,
          children: null,
        });
      }

      if (!loading && tags[id] !== undefined && isEmpty(rawChildren)) {
        prefixNodes.push({
          id: `${id}-no-children-message`,
          name: "No tags for this type",
          active: false,
          toggled: false,
          isEmptyStateMessage: true,
          children: null,
        });
      }

      prev[id] = {
        id,
        name,
        active: isViewingTagTypeExplore ? true : active,
        toggled:
          isViewingTagTypeExplore || activeTag?.type === id ? true : toggled,
        isTagType: true,
        children: [...prefixNodes, ...rawChildren],
      };

      return prev;
    },
    {}
  );

  return values(tagTypesTree);
};

export const resetTreeActiveNode = (
  tree: TagNavNode[],
  tag?: { id: string; type: string } | null
): TagNavNode[] => {
  const { id, type } = tag ?? {};

  if (!id || !type) {
    return tree.map((node) => ({
      ...node,
      active: false,
      toggled: false,
      children:
        node.children?.map((child) => ({ ...child, active: false })) ??
        node.children,
    })) as TagNavNode[];
  }

  return tree.map((node) => ({
    ...node,
    active: false,
    toggled: node.id === type,
    children:
      node.children?.map((child) => ({
        ...child,
        active: child.id === id,
      })) ?? node.children,
  })) as TagNavNode[];
};
